import { IInternalPlantItem } from "@/hooks/plantItems";

export const PLANT_UNIT_OF_MEASURE_MULTIPLICATION_FACTORS: Record<
  string,
  number
> = {
  WEEK5: 0.2,
  WEEK7: 0.14,
  HOURLY: 0.14,
  EA: 1,
  DAY: 0.14
};

export const getPlantAmount = ({
  unitOfMeasure,
  unitCost,
  quantity,
  noOfDays
}: {
  unitOfMeasure: string;
  unitCost: number;
  quantity: number;
  noOfDays: number;
}) =>
  unitCost *
  (PLANT_UNIT_OF_MEASURE_MULTIPLICATION_FACTORS[unitOfMeasure] || 0) *
  quantity *
  noOfDays;

export interface IPlantCategory {
  name: string;
  unitOfMeasure: string;
  unitCost: number;
  qtyAvailableToHire: number;
}

export const getPlantCategoryName = (item: IInternalPlantItem) =>
  item.internalPlantCategoryDescription?.trim() ?? "";

const groupByCategory = (items: IInternalPlantItem[]) =>
  groupBy(items.filter(getPlantCategoryName), getPlantCategoryName);

const groupBy = <T>(items: T[], key: (item: T) => string | number) => {
  const groups = new Map<string | number, T[]>();
  items.forEach((item) => {
    const group = groups.get(key(item));
    if (group) group.push(item);
    else groups.set(key(item), [item]);
  });
  return [...groups.values()];
};

/**
 * The items sharing the most common `key`. A tie goes to the group holding
 * the highest `value`, so when no value repeats the highest one wins.
 */
const mostCommonGroup = <T>(
  items: T[],
  key: (item: T) => string | number,
  value: (item: T) => number
) => {
  const highest = (group: T[]) => Math.max(...group.map(value));
  return groupBy(items, key).reduce((best, group) =>
    group.length > best.length ||
    (group.length === best.length && highest(group) > highest(best))
      ? group
      : best
  );
};

/**
 * Plant is forecast per category, not per item. A category's unit of measure
 * is the most common one among its items, and its unit cost the most common
 * cost among the items using that unit — falling back to the highest when
 * nothing repeats. Items without a category are left out.
 */
export const getPlantCategories = (items: IInternalPlantItem[]) =>
  new Map(
    groupByCategory(items).map((categoryItems) => {
      const name = getPlantCategoryName(categoryItems[0]);
      const sameUnit = mostCommonGroup(
        categoryItems,
        (item) => item.unitOfMeasure,
        (item) => item.unitCost
      );
      const sameCost = mostCommonGroup(
        sameUnit,
        (item) => item.unitCost,
        (item) => item.unitCost
      );
      const category: IPlantCategory = {
        name,
        unitOfMeasure: sameCost[0].unitOfMeasure,
        unitCost: sameCost[0].unitCost,
        qtyAvailableToHire: categoryItems.reduce(
          (sum, item) => sum + (item.qtyAvailableToHire ?? 0),
          0
        )
      };
      return [name, category];
    })
  );
